import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Button, Input, PasswordInput } from '@column/column-ui-kit';

import { HintLink } from '../../_components/HintLink';
import { Notification, NotificationList } from '~/components';
import { reCaptchaCheck } from '~/lib/recaptcha';
import { ROUTE } from '~/public/routes';
import { DashboardLoginResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Text } from '~/styles';
import { log, validateEmail } from '~/util';

interface PasswordAuthFormProps {
  showForgotPassword?: boolean;
  redirectUrl?: string;
  onSuccess: (response: DashboardLoginResponse) => void;
}

interface PasswordAuthFormValues {
  email: string;
  password: string;
}

export const PasswordAuthForm: React.FC<PasswordAuthFormProps> = ({
  showForgotPassword,
  redirectUrl,
  onSuccess: onSuccessCallback,
}) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleReCaptcha } = reCaptchaCheck();

  const { control, handleSubmit } = useForm<PasswordAuthFormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSuccess = ({ email, password }: PasswordAuthFormValues) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    handleReCaptcha(
      (reCaptchaToken: string) => {
        UserRepository.login(email, password, reCaptchaToken)
          .then((response) => {
            log({
              name: 'User logged in',
              context: { email },
            });

            onSuccessCallback(response);
          })
          .catch((error) => {
            setIsLoading(false);

            let message = 'An error occurred';
            if (error?.message) {
              message = error.message;
            } else if (error === 'Unauthorized') {
              message = 'Verification failed. Please try again and contact support if the problem persists.';
            }

            addDangerNotification({
              content: message,
              display: 'page-login',
            });
          });
      },
      (err) => {
        setIsLoading(false);

        addDangerNotification({
          content: err.message,
          display: 'page-login',
        });
      }
    );
  };

  return (
    <>
      <Text size={16} weight="semibold">
        Sign in
      </Text>

      <form onSubmit={handleSubmit(onSuccess)}>
        {redirectUrl && (
          <Notification color="info" withClose={false}>
            Sign in to view this page
          </Notification>
        )}
        <NotificationList display="page-login" spacing="top" />

        <Flex flexDirection="column" gap={16} mb={24}>
          <Controller<PasswordAuthFormValues, 'email'>
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: true,
              validate: validateEmail,
            }}
            render={({ field, fieldState: { error, isTouched } }) => (
              <Input placeholder="Email address" autoComplete="username" hasError={isTouched && !!error} {...field} />
            )}
          />
          <Controller<PasswordAuthFormValues, 'password'>
            name="password"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field, fieldState: { error, isTouched } }) => (
              <PasswordInput placeholder="Password" hasError={isTouched && !!error} {...field} />
            )}
          />
        </Flex>
        <Flex flexDirection="column" gap={16}>
          <Button fullWidth isLoading={isLoading}>
            Sign in
          </Button>
          {showForgotPassword && (
            <HintLink as={Link} to={ROUTE.FORGOT_PASSWORD}>
              Forgot your password?
            </HintLink>
          )}
        </Flex>
      </form>
    </>
  );
};
